import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.4,
      ease: 'easeIn'
    }
  }
};
const categories = ['Breakfast', 'Lunch', 'Drinks', 'Desserts'];
const menuData = {
  Breakfast: [{
    name: 'Avocado Smash Toast',
    desc: 'Sourdough, smashed avocado, cherry tomatoes, microgreens, chili flakes.',
    price: '$9.00'
  }, {
    name: 'Classic Breakfast Sandwich',
    desc: 'Brioche bun, folded egg, aged cheddar, thick-cut bacon, house aioli.',
    price: '$8.50'
  }, {
    name: 'Berry Acai Bowl',
    desc: 'Acai blend, house granola, fresh berries, banana, coconut flakes, honey.',
    price: '$10.00'
  }, {
    name: 'Overnight Oats',
    desc: 'Rolled oats, almond milk, chia seeds, maple syrup, seasonal fruit compote.',
    price: '$6.50'
  }, {
    name: 'Smoked Salmon Bagel',
    desc: 'Everything bagel, cream cheese, smoked salmon, capers, red onion, dill.',
    price: '$11.00'
  }],
  Lunch: [{
    name: 'Roasted Turkey Panini',
    desc: 'Ciabatta, roasted turkey, provolone, pesto, arugula, sun-dried tomatoes.',
    price: '$12.00'
  }, {
    name: 'Caprese Sandwich',
    desc: 'Baguette, fresh mozzarella, heirloom tomatoes, basil, balsamic glaze.',
    price: '$10.50'
  }, {
    name: 'Kale & Quinoa Salad',
    desc: 'Chopped kale, quinoa, roasted sweet potato, dried cranberries, lemon vinaigrette.',
    price: '$11.50'
  }, {
    name: 'Soup of the Day',
    desc: "Served with a slice of toasted sourdough bread. Ask about today's selection.",
    price: '$7.00'
  }, {
    name: 'Grilled Cheese & Tomato Soup',
    desc: 'Sourdough with sharp cheddar and gruyere, served with creamy tomato basil soup.',
    price: '$13.00'
  }],
  Drinks: [{
    name: 'Espresso',
    desc: 'Double shot of our house blend.',
    price: '$3.50'
  }, {
    name: 'Cappuccino',
    desc: 'Equal parts espresso, steamed milk, and milk foam.',
    price: '$4.50'
  }, {
    name: 'Lavender Honey Latte',
    desc: 'Espresso, steamed milk, house-made lavender syrup, local honey.',
    price: '$5.50'
  }, {
    name: 'Cold Brew',
    desc: 'Steeped for 18 hours, served over ice.',
    price: '$4.75'
  }, {
    name: 'Matcha Latte',
    desc: 'Ceremonial grade matcha, steamed milk, touch of vanilla.',
    price: '$5.50'
  }, {
    name: 'Artisan Tea Selection',
    desc: 'Earl Grey, Chamomile, Peppermint, or Jasmine Green.',
    price: '$3.50'
  }],
  Desserts: [{
    name: 'Matcha Rose Croissant',
    desc: 'Flaky butter croissant filled with matcha cream and rose petals.',
    price: '$4.75'
  }, {
    name: 'Dark Chocolate Brownie',
    desc: 'Fudgy brownie with sea salt flakes and dark chocolate chunks.',
    price: '$4.00'
  }, {
    name: 'Lemon Olive Oil Cake',
    desc: 'Moist slice of cake with a delicate lemon glaze.',
    price: '$5.00'
  }, {
    name: 'Classic Chocolate Chip Cookie',
    desc: 'Brown butter, semi-sweet chocolate, sea salt.',
    price: '$3.50'
  }, {
    name: 'Seasonal Fruit Tart',
    desc: 'Butter crust, vanilla bean custard, fresh seasonal berries.',
    price: '$6.00'
  }]
};
export function Menu() {
  const [activeTab, setActiveTab] = useState('Drinks');
  return <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="w-full min-h-screen py-24 bg-cafe-100" data-id="element-303">
      <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-304">
        <div className="text-center mb-16" data-id="element-305">
          <h1 className="font-serif text-5xl font-bold text-cafe-900 mb-6" data-id="element-306">
            Our Menu
          </h1>
          <p className="text-cafe-800/70 max-w-2xl mx-auto" data-id="element-307">
            Carefully curated and crafted with love. We use locally sourced
            ingredients whenever possible to bring you the freshest flavors.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-8 mb-12 border-b border-cafe-800/10 pb-4" data-id="element-308">
          {categories.map(category => <button key={category} onClick={() => setActiveTab(category)} className={`relative px-4 py-2 font-serif text-lg md:text-xl transition-colors ${activeTab === category ? 'text-cafe-900 font-bold' : 'text-cafe-800/60 hover:text-cafe-900'}`} data-id="element-309">
              {category}
              {activeTab === category && <motion.div layoutId="menu-tab-indicator" className="absolute -bottom-[17px] left-0 right-0 h-[2px] bg-cafe-sage" initial={false} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-310" />}
            </button>)}
        </div>

        {/* Menu Items */}
        <div className="min-h-[400px]" data-id="element-311">
          <AnimatePresence mode="wait" data-id="element-312">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-313">
              {menuData[activeTab as keyof typeof menuData].map((item, index) => <motion.div key={item.name} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className="group" data-id="element-314">
                    <div className="flex justify-between items-baseline mb-2 border-b border-cafe-800/10 pb-2 border-dashed" data-id="element-315">
                      <h3 className="font-serif text-xl font-bold text-cafe-900 group-hover:text-cafe-sage transition-colors" data-id="element-316">
                        {item.name}
                      </h3>
                      <span className="font-sans font-medium text-cafe-900 ml-4" data-id="element-317">
                        {item.price}
                      </span>
                    </div>
                    <p className="text-cafe-800/70 text-sm leading-relaxed" data-id="element-318">
                      {item.desc}
                    </p>
                  </motion.div>)}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-20 text-center text-sm text-cafe-800/60 bg-cafe-50 py-6 rounded-lg" data-id="element-319">
          <p data-id="element-320">
            Please inform our staff of any food allergies or dietary
            requirements before ordering.
          </p>
          <p className="mt-2" data-id="element-321">
            Oat, almond, and soy milk available upon request (+ $0.75).
          </p>
        </div>
      </div>
    </motion.div>;
}